import { voiceSupported } from './voice';

/** The longest voice note (matches VOICE_NOTE_MAX_SECONDS in @vixsync/shared). */
export const NOTE_MAX_MS = 60_000;

/** Formats the API accepts, best first: Chrome/Firefox record Opus, Safari records MP4/AAC. */
const TYPES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'];

/** Voice notes need the microphone (allowed on this page) and MediaRecorder. */
export function noteRecordingSupported(): boolean {
  return voiceSupported() && typeof MediaRecorder !== 'undefined';
}

export interface RecordedNote {
  blob: Blob;
  durationMs: number;
}

/** Records one voice note from the microphone. */
export class NoteRecorder {
  private stream?: MediaStream;
  private recorder?: MediaRecorder;
  private chunks: Blob[] = [];
  private startedAt = 0;

  async start() {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    const mimeType = TYPES.find((t) => MediaRecorder.isTypeSupported?.(t));
    this.recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data.size) this.chunks.push(e.data);
    };
    this.recorder.start(250);
    this.startedAt = Date.now();
  }

  elapsed() {
    return this.startedAt ? Date.now() - this.startedAt : 0;
  }

  stop(): Promise<RecordedNote> {
    const recorder = this.recorder;
    const durationMs = Math.min(this.elapsed(), NOTE_MAX_MS);
    return new Promise((resolve) => {
      const done = () => {
        this.release();
        const type = recorder?.mimeType || this.chunks[0]?.type || 'audio/webm';
        resolve({ blob: new Blob(this.chunks, { type }), durationMs });
      };
      if (!recorder || recorder.state === 'inactive') return done();
      recorder.onstop = done;
      recorder.stop();
    });
  }

  cancel() {
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.release();
  }

  private release() {
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = undefined;
  }
}
